import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

// Replacement for react-modal-video (unmaintained, no React 19 peer range).
// Same markup and class names (styles: src/css/modal-video.scss, copied from
// the library), same embed URLs and query defaults, Esc / backdrop click to
// close, Tab cycling between the dialog and the player, and the same
// fit-to-window width for a 16:9 frame.
interface ModalVideoProps {
  channel: "youtube" | "vimeo";
  isOpen: boolean;
  videoId: string;
  /** Start offset as stored in Strapi: "6m24s", "1h2m3s", "90s" or "90" */
  start?: string;
  onClose?: () => void;
}

// The library's default player options, in its order, with null entries dropped
const QUERY = {
  youtube:
    "autoplay=1&cc_load_policy=1&controls=1&disablekb=0&enablejsapi=0&fs=1&iv_load_policy=1&loop=0&rel=0&showinfo=1&start=0&wmode=transparent&theme=dark&mute=0",
  vimeo: "api=false&autopause=true&autoplay=true&byline=true&loop=false&portrait=true&title=true&xhtml=false",
};

// "6m24s" / "1h2m3s" / "90s" / "90" → seconds; null when unparseable
export const startSeconds = (start?: string | null): number | null => {
  const value = (start || "").trim();
  if (!value) return null;
  if (/^\d+$/.test(value)) return Number(value);
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(value);
  if (!m || !(m[1] || m[2] || m[3])) return null;
  return Number(m[1] || 0) * 3600 + Number(m[2] || 0) * 60 + Number(m[3] || 0);
};

// The start offset goes where each player reads it: Vimeo's #t= fragment after
// the query, YouTube's start= parameter.
export const videoUrl = (channel: ModalVideoProps["channel"], videoId: string, start?: string) => {
  const seconds = startSeconds(start);
  if (channel === "vimeo") {
    return `//player.vimeo.com/video/${videoId}?${QUERY.vimeo}${seconds !== null ? `#t=${seconds}s` : ""}`;
  }
  const query = seconds !== null ? QUERY.youtube.replace("start=0", `start=${seconds}`) : QUERY.youtube;
  return `//www.youtube.com/embed/${videoId}?${query}`;
};

// 16:9 frame: full width unless that would be taller than the window
const frameWidth = () => {
  const { innerWidth, innerHeight } = window;
  return innerHeight < innerWidth * (9 / 16) ? Math.floor((16 / 9) * innerHeight) : "100%";
};

export default function ModalVideo({ channel, isOpen, videoId, start, onClose }: ModalVideoProps) {
  const [width, setWidth] = useState<number | string>(frameWidth);
  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => onClose?.(), [onClose]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    let timeout: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => setWidth(frameWidth()), 10);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(timeout);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [close]);

  useEffect(() => {
    if (isOpen) modalRef.current?.focus();
  }, [isOpen]);

  const cycleFocus = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    e.stopPropagation();
    if (document.activeElement === modalRef.current) iframeRef.current?.focus();
    else if (document.activeElement === closeRef.current) modalRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-video"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="You just opened the modal video"
      onClick={close}
      onKeyDown={cycleFocus}
      ref={modalRef}
    >
      <div className="modal-video-body">
        <div className="modal-video-inner" style={{ width }}>
          <div className="modal-video-movie-wrap" style={{ paddingBottom: "56.25%" }}>
            <iframe
              width="460"
              height="230"
              src={videoUrl(channel, videoId, start)}
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              tabIndex={-1}
              ref={iframeRef}
              title="Video player"
            />
            <button
              type="button"
              className="modal-video-close-btn"
              aria-label="Close the modal by clicking here"
              ref={closeRef}
              onKeyDown={cycleFocus}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
